// String literals match constants/meetings Periods/Statuses; kept local so partial
// test mocks of constants/meetings do not break module init when this file is loaded
// via features/meetings/index → export_button.
const PERIOD_ALL_TIME = 'all_time'
const PERIOD_FIXED = 'fixed'
const PERIOD_PAST = 'past'
const PERIOD_THIS_MONTH = 'this_month'
const PERIOD_LAST_WEEK = 'last_week'
const PERIOD_THIS_WEEK = 'this_week'
const PERIOD_THIS_YEAR = 'this_year'
const PERIOD_TODAY = 'today'
const PERIOD_UPCOMING = 'upcoming'

const STATUS_ACTIVE = 'active'
const STATUS_ALL = 'all'
const STATUS_CANCELED = 'canceled'
const STATUS_NO_SHOW = 'no_show'
const STATUS_RESCHEDULED = 'rescheduled'

const EXPORT_PERIODS = new Set([
  PERIOD_FIXED,
  PERIOD_TODAY,
  PERIOD_THIS_WEEK,
  PERIOD_LAST_WEEK,
  PERIOD_THIS_MONTH,
  PERIOD_THIS_YEAR,
  PERIOD_ALL_TIME,
  PERIOD_UPCOMING,
  PERIOD_PAST,
])

/**
 * Coerced to all_time: unsupported list periods, and "upcoming" (meetings list loads
 * past + future via split queries; export PeriodService "upcoming" is future-only).
 */
const PERIODS_COERCED_TO_ALL_TIME = new Set(['future', PERIOD_UPCOMING])

/** Exported as an explicit fixed range rather than a period name — see mapPeriodForExport. */
const WEEK_PRESET_PERIODS = new Set([PERIOD_THIS_WEEK, PERIOD_LAST_WEEK])

const EXPORT_STATUS_IDS = new Set([
  STATUS_ACTIVE,
  STATUS_CANCELED,
  STATUS_NO_SHOW,
  STATUS_RESCHEDULED,
  STATUS_ALL,
])

const SEARCH_ONLY_KEYS = new Set([
  'scheduling_methods',
  'query',
  'search_keyword',
  'meeting_intelligence_status',
  'role',
  'event_type_uuids',
  'page',
  'status_explicitly_set',
  'deletedRecapId',
])

const UUID_ID_PAIRS = [
  ['user_uuids', 'user_ids'],
  ['team_uuids', 'team_ids'],
  ['user_group_uuids', 'user_group_ids'],
]

const EXPORT_ARRAY_KEYS = [
  'user_ids',
  'user_uuids',
  'team_ids',
  'team_uuids',
  'user_group_ids',
  'user_group_uuids',
  'event_type_ids',
  'package_uuids',
  'invitee_emails',
  'utm_campaign',
  'utm_source',
  'utm_medium',
  'utm_content',
  'utm_term',
]

const hasNonEmptyArray = (value) => Array.isArray(value) && value.length > 0

const mapPeriodForExport = (
  period,
  startDate,
  endDate,
  { forwardOnly = false, presetPeriod = null, presetDateRange = null } = {},
) => {
  const effectivePeriod = presetPeriod || period

  // The week chips export as an explicit fixed range so the CSV covers exactly the dates shown in
  // the list. Sending `this_week` / `last_week` would let the export service apply its own week
  // definition, which no longer matches the page's Monday–Sunday window (SA-4357).
  if (WEEK_PRESET_PERIODS.has(effectivePeriod) && presetDateRange?.startDate && presetDateRange?.endDate) {
    return {
      period: PERIOD_FIXED,
      start_date: presetDateRange.startDate,
      end_date: presetDateRange.endDate,
    }
  }

  let mappedPeriod = effectivePeriod
  // The Upcoming preset (forward-only) shows today + upcoming with no past meetings, which
  // matches the export's native forward-only "upcoming" period — so don't coerce it. Outside
  // the preset, the upcoming list loads past + future via split queries, so it stays all_time.
  const isPresetUpcoming = forwardOnly && effectivePeriod === PERIOD_UPCOMING
  const coerceToAllTime = !EXPORT_PERIODS.has(effectivePeriod)
    || (PERIODS_COERCED_TO_ALL_TIME.has(effectivePeriod) && !isPresetUpcoming)
  if (coerceToAllTime) {
    mappedPeriod = PERIOD_ALL_TIME
  }

  const result = { period: mappedPeriod }
  if (mappedPeriod === PERIOD_FIXED && startDate && endDate) {
    result.start_date = startDate
    result.end_date = endDate
  }
  return result
}

/** Pass through export-supported status filters; default to active when unset. */
const mapStatusIdsForExport = (statusIds) => {
  if (!hasNonEmptyArray(statusIds)) {
    return [STATUS_ACTIVE]
  }

  const filtered = statusIds.filter((status) => EXPORT_STATUS_IDS.has(status))
  if (!filtered.length) {
    return [STATUS_ACTIVE]
  }

  if (filtered.includes(STATUS_ALL)
    || (filtered.includes(STATUS_ACTIVE) && filtered.includes(STATUS_CANCELED))) {
    return []
  }

  return filtered
}

const EXPORT_HOST_IDS = new Set(['host', 'non_host', 'my_meetings', 'permission'])

/**
 * Passes meetings-page host filter values through to scheduled_events export.
 * ExportHostService applies my_meetings / permission on the current user (aligned
 * with list role mapping), including when scoped to another user or a team.
 */
const mapHostIdsForExport = (hostIds) => {
  if (!hasNonEmptyArray(hostIds)) {
    return {}
  }

  if (hostIds.length === 1 && hostIds[0] === 'all') {
    return {}
  }

  const mapped = [...new Set(hostIds.filter((hostId) => EXPORT_HOST_IDS.has(hostId)))]
  return mapped.length ? { host_ids: mapped } : {}
}

const applyUuidPreference = (params) => {
  const result = { ...params }
  UUID_ID_PAIRS.forEach(([uuidKey, idKey]) => {
    if (hasNonEmptyArray(result[uuidKey])) {
      delete result[idKey]
    }
  })
  return result
}

const pickExportArrayFields = (params) => (
  EXPORT_ARRAY_KEYS.reduce((accumulator, key) => {
    if (hasNonEmptyArray(params[key])) {
      accumulator[key] = params[key]
    }
    return accumulator
  }, {})
)

/**
 * Normalizes meetings route/tab scope to scheduled_events export scope_type / scope_id.
 * Mirrors getTabScopeV2 in hooks/home/tabs.js.
 */
export const normalizeScheduledEventsExportScope = (scopeType, scopeId) => {
  if (!scopeType) {
    return { type: null, id: undefined }
  }

  switch (scopeType) {
    case 'all_users':
      return { type: 'users', id: undefined }
    case 'user':
      return scopeId
        ? { type: 'user', id: scopeId }
        : { type: 'users', id: undefined }
    case 'team':
      return scopeId
        ? { type: 'team', id: scopeId }
        : { type: 'teams', id: undefined }
    case 'users':
    case 'teams':
    case 'all_users_and_teams':
      return { type: scopeType, id: undefined }
    case 'user_group':
      return { type: scopeType, id: scopeId || undefined }
    default:
      return { type: scopeType, id: scopeId || undefined }
  }
}

/**
 * Maps meetings page filter params (from useFilters + lookup_uuids hydration) to
 * scheduled_events export API query params. Export replays scope + filters server-side
 * so all matching meetings are included, not only loaded list pages.
 */
export const mapMeetingsFilterParamsToExportParams = (
  filterParams,
  { forwardOnly = false, presetPeriod = null, presetDateRange = null } = {},
) => {
  if (!filterParams) {
    return { period: PERIOD_ALL_TIME, status_ids: [STATUS_ACTIVE] }
  }

  const withoutSearchOnly = Object.fromEntries(
    Object.entries(filterParams).filter(([key]) => !SEARCH_ONLY_KEYS.has(key)),
  )

  const withUuidPreference = applyUuidPreference(withoutSearchOnly)
  const { period, start_date: startDate, end_date: endDate, status_ids: statusIds } = withUuidPreference
  const mappedStatusIds = mapStatusIdsForExport(statusIds)

  return {
    ...mapPeriodForExport(period, startDate, endDate, { forwardOnly, presetPeriod, presetDateRange }),
    ...(mappedStatusIds.length ? { status_ids: mappedStatusIds } : {}),
    ...mapHostIdsForExport(withUuidPreference.host_ids),
    ...pickExportArrayFields(withUuidPreference),
  }
}
